// @givmo/app-admin — Employees → ROSTER tab (07-1.5a). The company-roster surface.
//
// SaaS re-skin (R2): the same data wiring + behavior + testids as the parity rebuild, re-presented on
// the design-system SaaS components (SearchInput · a selection toolbar · DataTable with a checkbox
// column · per-row danger remove · TablePagination-style load-more · EmptyState). A RE-SKIN, not a
// rewrite — the api-client hooks, the `filter[ids]` chunked multi-remove, the debounced search, the
// select-all/selection logic, the infinite-scroll sentinel, the ConfirmDialog guard, and every
// `data-testid` are unchanged.
//
// EP-WEB8 moved this body OUT of `employees-page.tsx` (which is now the tabbed shell holding the page
// root testid + PageHeader) and changed NOTHING else — the same JSX, the same hooks in the same order,
// the same ids. `employees.list.react.spec.ts` is a BLOCKING e2e spec pinning `employees-search` and
// `employees-empty-state`, and Roster is the default tab, so both are on screen at load exactly as
// before. Keep it that way: making Roster non-default, or lazy behind a click, breaks that spec.
//
// Wiring:
//   - LIST   → `companiesGetCompanyEmployees` (GET /admin/company/employees) via `useInfiniteQuery`.
//              Page size 10; `q` OMITTED when the search box is empty.
//   - REMOVE → `companiesRemoveEmployeesFromCompany` by `filter[ids]`, CHUNKED at 100 per request,
//              always behind the ConfirmDialog. On success: invalidate, clear selection, toast.
//
// EP-WEB11 added the OCCASION DATES affordances — a "Dates on file" column, a per-row date dialog,
// and a CSV mass-import — all three behind `isCompanyOccasionProgramsEnabled()`. Two properties of
// that gate matter here:
//
//   1. This page is LIVE on prod and nothing else gates it, so flag-OFF must be byte-invisible: no
//      column, no toolbar button, no row action, and no occasion request. That invariant is pinned
//      by `roster-occasion-dates.test.tsx`.
//   2. The build-time switch and the backend's `COMPANY_OCCASION_PROGRAMS_ENABLED` flip
//      INDEPENDENTLY, so an occasion route can 404 while these controls are visible. That is skew,
//      not failure: the first 404 latches `occasionUnavailable` and both dialogs swap to a
//      considered "not available in this environment" notice instead of raising an error toast.
//
// AS-BE96 (D-AS-70, question 4) added the Owner's per-employee "Reset company sign-in" row action:
// the backend clears that employee's company sign-in connection and keeps the employment, and the
// person connects their work account again themselves. Its whole gate is SERVER-ANSWERED, like the
// two-factor column: `sso_linked` rides the roster read, served only to an Owner and only while
// company sign-in is on, and only an explicit `true` draws the control (`readSsoLinked`). A roster
// with no connected row is byte-unchanged: the action column keeps today's width and class.
//
// The dates themselves ride the roster read (`CompanyEmployeeOut.birthday_month` /
// `.birthday_day` / `.hire_date`, added by EP-BE11), so the column and the dialog's prefill cost no
// extra request — and a successful write just invalidates this list.
//
// ★ D-EP-27: the CONSUMER `birth_date` is a different fact and the employer must never see it. The
// roster row no longer carries it (Company Profiles' CAM-R141; their CAM-R170 may restore it), though
// the committed snapshot declares it until its next regen; this package's `eslint.config.mjs` (as
// `apps/admin`'s) fails the lint on any read of it.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  useInfiniteQuery,
  useMutation,
  useQueryClient,
  type QueryFunctionContext,
} from '@tanstack/react-query';
import {
  sdk,
  type CompaniesRemoveEmployeesFromCompanyData,
  type CompanyEmployeeOut,
  isGivmoError,
} from '@givmo/api-client';
import {
  Avatar,
  Badge,
  Button,
  Checkbox,
  cn,
  DataTable,
  EmptyState,
  Icon,
  IconButton,
  SearchInput,
  toast,
  type DataTableColumn,
} from '@givmo/design-system';
import { Briefcase, Cake, CalendarDays, FileUp, RotateCcwKey, Trash2, Users } from 'lucide-react';
import { extractList } from '@givmo/api-client/list';
import { ConfirmDialog } from '../../components/confirm-dialog';
import { isCompanyOccasionProgramsEnabled } from '../../lib/env';
import {
  readSsoIdentityRelease,
  readSsoIdentityReleaseEmailSent,
} from '../security/company-security-decode';
import { releaseEmployeeSsoIdentity } from '../security/company-security-transport';
import { SSO_COPY, SSO_COPY_DRAFT, releaseActionLabel } from '../security/security-copy';
import {
  mfaStatusPresentation,
  readMfaStatus,
  readSsoLinked,
  readSsoLockActive,
  rosterShowsMfaColumn,
} from '../security/security-decode';
import { useTwoFactorRequirementLive } from '../security/security-probes';
import { employeeFullName, removalCancelsRequestsNote } from './employees-decode';
import { ImportOccasionDatesDialog } from './import-occasion-dates-dialog';
import { OccasionDatesDialog } from './occasion-dates-dialog';
import { formatBirthdayLong, formatHireDateLong, occasionDatesCell } from './occasion-dates-decode';

const PREFIX = 'employees';
const OCCASION_PREFIX = 'roster-occasion';
const MFA_PREFIX = 'roster-mfa';
const SSO_RESET_PREFIX = 'roster-sso-reset';
const PAGE_LIMIT = 10;
const REMOVE_CHUNK_SIZE = 100;
const SEARCH_DEBOUNCE_MS = 400;

const LIST_KEY = (q: string) => ['companies-get_company_employees', 'infinite', q] as const;

async function fetchEmployeesPage(
  q: string,
  offset: number,
  signal: AbortSignal,
): Promise<CompanyEmployeeOut[]> {
  const trimmed = q.trim();
  const { data } = await sdk.companiesGetCompanyEmployees({
    query: {
      ...(trimmed.length > 0 ? { q: trimmed } : {}),
      'page[limit]': PAGE_LIMIT,
      'page[offset]': offset,
    },
    signal,
    throwOnError: true,
  });
  return extractList<CompanyEmployeeOut>(data);
}

async function removeEmployeeIds(ids: string[]): Promise<void> {
  if (ids.length === 0) return;
  for (let i = 0; i < ids.length; i += REMOVE_CHUNK_SIZE) {
    const chunk = ids.slice(i, i + REMOVE_CHUNK_SIZE);
    await sdk.companiesRemoveEmployeesFromCompany({
      query: {
        'filter[ids]': chunk.join(','),
      } as unknown as CompaniesRemoveEmployeesFromCompanyData['query'],
      throwOnError: true,
    });
  }
}

type PendingRemove =
  | { kind: 'single'; employee: CompanyEmployeeOut }
  | { kind: 'multi'; employees: CompanyEmployeeOut[] };

export function RosterTab() {
  const queryClient = useQueryClient();

  const [searchInput, setSearchInput] = useState('');
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [pending, setPending] = useState<PendingRemove | null>(null);

  // ── Occasion dates (EP-WEB11), entirely absent while the switch is off. ────────────────────────
  const occasionEnabled = isCompanyOccasionProgramsEnabled();
  const [datesFor, setDatesFor] = useState<CompanyEmployeeOut | null>(null);
  const [importOpen, setImportOpen] = useState(false);
  /** Latched by the first 404 from either occasion write — the routes are dark in this env. */
  const [occasionUnavailable, setOccasionUnavailable] = useState(false);

  useEffect(() => {
    const handle = window.setTimeout(() => setQuery(searchInput), SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(handle);
  }, [searchInput]);

  const list = useInfiniteQuery({
    queryKey: LIST_KEY(query),
    queryFn: ({ pageParam, signal }: QueryFunctionContext<ReturnType<typeof LIST_KEY>, number>) =>
      fetchEmployeesPage(query, pageParam, signal),
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) =>
      lastPage.length < PAGE_LIMIT ? undefined : allPages.reduce((n, p) => n + p.length, 0),
  });

  const employees = useMemo(() => (list.data ? list.data.pages.flat() : []), [list.data]);

  // ── Two-factor status (AS-WEB5) — a SERVER-ANSWERED column, with no switch of its own. ─────────
  // `mfa_status` rides the roster read, is disclosed by the backend only to an Owner (a Member reads
  // `null` on every row), and reads `not_required` for everyone while the employer's effective tier
  // is below `require`. `rosterShowsMfaColumn` is therefore the whole gate: the column appears
  // exactly when somebody on the page is actually being asked for a second factor, which keeps this
  // LIVE page unchanged for every company that is not asking — and unchanged for a Member, and
  // unchanged against a backend that predates the field.
  const mfaColumnVisible = useMemo(() => rosterShowsMfaColumn(employees), [employees]);
  // AS-WEB20 (D-AS-39, fix 4): under the company two-factor requirement there is no grace period, so
  // the column says "Not set up" where it said "Past grace period". The signal is the Security
  // probe's (the auth-policy read's count key), started only once the column shows, so a roster
  // asking nobody spends no request.
  const requirementLive = useTwoFactorRequirementLive(mfaColumnVisible);

  // ── One employee's company sign-in reset (AS-BE96) — server-answered, like the column above. ──
  // `sso_linked` is served to an Owner only, while company sign-in is on, so a row offers the reset
  // exactly when the server says there is a connection to reset. The row's own `sso_lock_active`
  // decides the confirmation's lock sentence, per person (review-80 F4): a break-glass admin keeps
  // personal sign-in under the lock, so it is never said of one.
  const ssoResetOffered = useMemo(() => employees.some((e) => readSsoLinked(e)), [employees]);
  const [releaseFor, setReleaseFor] = useState<CompanyEmployeeOut | null>(null);

  useEffect(() => {
    setSelected((prev) => {
      if (prev.size === 0) return prev;
      const loaded = new Set(employees.map((e) => e.id));
      const next = new Set([...prev].filter((id) => loaded.has(id)));
      return next.size === prev.size ? prev : next;
    });
  }, [employees]);

  const allSelected = employees.length > 0 && selected.size === employees.length;

  const toggleAll = useCallback(
    (checked: boolean) => {
      setSelected(checked ? new Set(employees.map((e) => e.id)) : new Set());
    },
    [employees],
  );

  const toggleOne = useCallback((id: string, checked: boolean) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (checked) next.add(id);
      else next.delete(id);
      return next;
    });
  }, []);

  /** Prefix invalidation, so an import that touched rows outside the current search is not stale. */
  const invalidateRoster = () => {
    void queryClient.invalidateQueries({ queryKey: ['companies-get_company_employees'] });
  };

  const remove = useMutation({
    mutationFn: (ids: string[]) => removeEmployeeIds(ids),
    onSuccess: async (_data, ids) => {
      setPending(null);
      setSelected(new Set());
      toast.success(ids.length === 1 ? 'Employee removed' : `${ids.length} employees removed`);
      await queryClient.invalidateQueries({ queryKey: LIST_KEY(query) });
    },
    onError: (error: unknown) => {
      const message = isGivmoError(error)
        ? error.message
        : 'Something went wrong. Please try again.';
      toast.error(message);
    },
  });

  const release = useMutation({
    mutationFn: (employeeUserId: string) => releaseEmployeeSsoIdentity(employeeUserId),
    onSuccess: async (body) => {
      setReleaseFor(null);
      const released = readSsoIdentityRelease(body);
      if (released === true) {
        // Says the employee was emailed only when the server says so (review-80 F4).
        const emailSent = readSsoIdentityReleaseEmailSent(body);
        toast.success(
          emailSent === true
            ? SSO_COPY.releaseDone
            : emailSent === false
              ? SSO_COPY.releaseDoneNotEmailed
              : SSO_COPY.releaseDoneEmailUnknown,
        );
      } else if (released === false) toast(SSO_COPY.releaseNothing);
      else toast(SSO_COPY.releaseUnconfirmed);
      await queryClient.invalidateQueries({ queryKey: LIST_KEY(query) });
    },
    onError: (error: unknown) => {
      toast.error(isGivmoError(error) ? error.message : SSO_COPY.releaseFailed);
    },
  });

  const releaseLockActive = releaseFor === null ? null : readSsoLockActive(releaseFor);
  const releaseNote =
    releaseLockActive === true
      ? SSO_COPY_DRAFT.releaseConfirmLock
      : releaseLockActive === null
        ? SSO_COPY_DRAFT.releaseConfirmLockMaybe
        : undefined;

  const confirmRemove = () => {
    if (!pending) return;
    const ids =
      pending.kind === 'single' ? [pending.employee.id] : pending.employees.map((e) => e.id);
    remove.mutate(ids);
  };

  const dialogSubject =
    pending === null
      ? undefined
      : pending.kind === 'single'
        ? employeeFullName(pending.employee)
        : `${pending.employees.length} employees selected`;

  // ── Sentinel-driven pagination: fetch the next page when the bottom marker scrolls into view. ──
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const node = sentinelRef.current;
    if (!node || !list.hasNextPage) return;
    const observer = new IntersectionObserver((entries) => {
      if (entries[0]?.isIntersecting && list.hasNextPage && !list.isFetchingNextPage) {
        void list.fetchNextPage();
      }
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, [list.hasNextPage, list.isFetchingNextPage, list.fetchNextPage]);

  // ── Columns: checkbox · name+avatar · work email · per-row danger remove. ──────────────────────
  // Memoized so rows skip re-render on unrelated parent state (search keystrokes, dialog opens). The
  // deps are exactly what the cells close over, so the identity still changes on a real change.
  const columns: DataTableColumn<CompanyEmployeeOut>[] = useMemo(
    () => [
      {
        id: 'select',
        width: '48px',
        headerClassName: 'pr-0',
        header: (
          <Checkbox
            checked={allSelected}
            onCheckedChange={toggleAll}
            aria-label="Select all employees"
            testId={`${PREFIX}-select-all`}
          />
        ),
        cell: (employee) => (
          <Checkbox
            checked={selected.has(employee.id)}
            onCheckedChange={(checked) => toggleOne(employee.id, checked)}
            aria-label={`Select ${employeeFullName(employee)}`}
            testId={`${PREFIX}-row-${employee.id}-checkbox`}
          />
        ),
      },
      {
        id: 'name',
        header: 'Name',
        width: '40%',
        cell: (employee) => (
          <div className="flex min-w-0 items-center gap-12">
            <Avatar
              name={employeeFullName(employee)}
              src={employee.logo_url ?? undefined}
              size="sm"
            />
            <span className="truncate font-w600 text-text-strong">
              {employeeFullName(employee)}
            </span>
          </div>
        ),
      },
      {
        id: 'work_email',
        header: 'Work email',
        truncate: true,
        cell: (employee) => (
          <span className="text-text-secondary">{employee.work_email ?? '—'}</span>
        ),
      },
      // "Dates on file" — a presence indicator, not an editor. Reads the three EP-BE11 fields off the
      // row; absent → em-dash. The whole column is gone while the switch is off.
      ...(occasionEnabled
        ? [
            {
              id: 'occasion_dates',
              header: 'Dates on file',
              width: '180px',
              cell: (employee) => <OccasionDatesCell employee={employee} />,
            } as DataTableColumn<CompanyEmployeeOut>,
          ]
        : []),
      {
        id: 'status',
        header: 'Status',
        width: '120px',
        cell: (employee) =>
          employee.status === 'Active' ? (
            <Badge tone="success" dot>
              Active
            </Badge>
          ) : (
            <Badge tone="neutral" dot>
              {employee.status}
            </Badge>
          ),
      },
      // "Two-factor" (AS-WEB5) — the employee's standing against the company's policy, straight from
      // the server. Not sortable: the backend computes `mfa_status` over the rows it already
      // paginated, so there is no sort key behind it and a header that offered one would reorder
      // nothing. A row whose status was not disclosed renders an em-dash, never a guess.
      ...(mfaColumnVisible
        ? [
            {
              id: 'mfa_status',
              header: 'Two-factor',
              width: '170px',
              cell: (employee) => (
                <MfaStatusCell employee={employee} requirementLive={requirementLive} />
              ),
            } as DataTableColumn<CompanyEmployeeOut>,
          ]
        : []),
      {
        id: 'actions',
        header: '',
        align: 'right',
        // 40px per icon button beyond the first; the reset widens the column for the whole page once
        // ANY row offers it, so the column does not change width from row to row.
        width: `${56 + 40 * ((occasionEnabled ? 1 : 0) + (ssoResetOffered ? 1 : 0))}px`,
        cell: (employee) => (
          // The gap is conditional so that flag-off leaves this cell's class attribute BYTE-IDENTICAL
          // to `development`. With one child it makes no visual difference, but "byte-invisible" is
          // the claim this program makes about the live page, and a diffable DOM is how it is checked.
          <div className={cn('flex justify-end', (occasionEnabled || ssoResetOffered) && 'gap-4')}>
            {/* AS-BE96: drawn only where the server says this employee's company sign-in is
                connected. Revealed on hover like its neighbours. */}
            {readSsoLinked(employee) && (
              <IconButton
                aria-label={releaseActionLabel(employeeFullName(employee))}
                tone="ghost"
                size="sm"
                disabled={release.isPending}
                onClick={() => setReleaseFor(employee)}
                className={cn(
                  'opacity-0 transition-opacity focus-visible:opacity-100 group-hover:opacity-100 pointer-coarse:opacity-100',
                  release.isPending && 'opacity-100',
                )}
                testId={`${SSO_RESET_PREFIX}-row-${employee.id}`}
              >
                <Icon icon={RotateCcwKey} size={16} aria-hidden />
              </IconButton>
            )}
            {/* Revealed on row-hover (and on keyboard focus + while a remove is pending, for a11y), and
                always shown on a touch screen, which has no hover (EP-WEB42, `pointer-coarse`). */}
            {occasionEnabled && (
              <IconButton
                aria-label={`Edit occasion dates for ${employeeFullName(employee)}`}
                tone="ghost"
                size="sm"
                onClick={() => setDatesFor(employee)}
                className="opacity-0 transition-opacity focus-visible:opacity-100 group-hover:opacity-100 pointer-coarse:opacity-100"
                testId={`${OCCASION_PREFIX}-row-${employee.id}-edit`}
              >
                <Icon icon={CalendarDays} size={16} aria-hidden />
              </IconButton>
            )}
            <IconButton
              aria-label={`Remove ${employeeFullName(employee)}`}
              tone="danger"
              size="sm"
              disabled={remove.isPending}
              onClick={() => setPending({ kind: 'single', employee })}
              className={cn(
                'opacity-0 transition-opacity focus-visible:opacity-100 group-hover:opacity-100 pointer-coarse:opacity-100',
                remove.isPending && 'opacity-100',
              )}
              testId={`${PREFIX}-row-${employee.id}-remove`}
            >
              <Icon icon={Trash2} size={16} aria-hidden />
            </IconButton>
          </div>
        ),
      },
    ],
    [
      allSelected,
      mfaColumnVisible,
      occasionEnabled,
      release.isPending,
      requirementLive,
      remove.isPending,
      selected,
      ssoResetOffered,
      toggleAll,
      toggleOne,
    ],
  );

  const selectionCount = selected.size;

  return (
    <div className="flex flex-col gap-24">
      <DataTable
        columns={columns}
        rows={employees}
        rowKey={(e) => e.id}
        rowTestId={(e) => `${PREFIX}-row-${e.id}`}
        loading={list.isPending}
        error={
          list.isError
            ? isGivmoError(list.error)
              ? list.error.message
              : 'Failed to load employees.'
            : undefined
        }
        onRetry={() => void list.refetch()}
        toolbar={
          <>
            {/* Left: search at rest; a "{n} selected · Clear" cluster while rows are selected. */}
            {selectionCount > 0 ? (
              <div className="flex items-center gap-10">
                <span className="font-sans text-s14 font-w600 tracking-givmo text-text-strong tabular-nums">
                  {selectionCount} selected
                </span>
                <Button
                  appearance="saas"
                  tone="ghost"
                  size="sm"
                  onClick={() => setSelected(new Set())}
                >
                  Clear
                </Button>
              </div>
            ) : (
              <SearchInput
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onClear={() => setSearchInput('')}
                placeholder="Search employees…"
                aria-label="Search users"
                testId={`${PREFIX}-search`}
              />
            )}
            {/* Occasion dates by CSV. The backend applies the whole file and echoes every row, so
                this opens a dialog rather than fanning out a request per employee. */}
            {occasionEnabled && (
              <Button
                appearance="saas"
                tone="secondary"
                size="sm"
                leadingIcon={<Icon icon={FileUp} size={14} aria-hidden />}
                onClick={() => setImportOpen(true)}
                testId={`${OCCASION_PREFIX}-import`}
              >
                Import occasion dates
              </Button>
            )}
            {/* Bulk remove: always in the DOM (testid contract) but quiet + disabled at rest — the
                solid-red danger CTA only appears once a row is selected, labelled with the count. */}
            <Button
              appearance="saas"
              tone={selectionCount > 0 ? 'danger' : 'ghost'}
              size="sm"
              disabled={selectionCount === 0 || remove.isPending}
              leadingIcon={<Icon icon={Trash2} size={14} aria-hidden />}
              onClick={() =>
                setPending({
                  kind: 'multi',
                  employees: employees.filter((e) => selected.has(e.id)),
                })
              }
              testId={`${PREFIX}-remove-selected`}
            >
              {selectionCount > 0 ? `Remove ${selectionCount}` : 'Remove'}
            </Button>
          </>
        }
        emptyState={
          <EmptyState
            testId={`${PREFIX}-empty-state`}
            icon={<Icon icon={Users} size={20} aria-hidden />}
            title="No users found"
            description={
              query.trim().length > 0
                ? 'No employees match your search. Try a different name or email.'
                : 'Employees who join your company program will appear here.'
            }
          />
        }
        footer={
          list.hasNextPage ? (
            <>
              <span className="font-sans text-s13 tracking-normal text-text-tertiary tabular-nums">
                Showing {employees.length} employees
              </span>
              <div className="flex items-center gap-12">
                {/* Sentinel for infinite scroll + an explicit Load more (keeps the testid contract). */}
                <div ref={sentinelRef} aria-hidden className="size-px" />
                <Button
                  appearance="saas"
                  tone="secondary"
                  size="sm"
                  disabled={list.isFetchingNextPage}
                  onClick={() => void list.fetchNextPage()}
                  testId={`${PREFIX}-load-more`}
                >
                  {list.isFetchingNextPage ? 'Loading…' : 'Load more'}
                </Button>
              </div>
            </>
          ) : undefined
        }
      />

      <ConfirmDialog
        open={pending !== null}
        onOpenChange={(open) => {
          if (!open && !remove.isPending) setPending(null);
        }}
        title="Are you sure you want to remove employee(s)?"
        subject={dialogSubject}
        body="Removing a user will disconnect the user’s Givmo account from your company, rendering any remaining annual match budget forfeited, but their account is not deleted off of Givmo."
        note={
          pending === null
            ? undefined
            : removalCancelsRequestsNote(
                pending.kind === 'single' ? [pending.employee] : pending.employees,
              )
        }
        confirmLabel="Remove"
        destructive
        loading={remove.isPending}
        onConfirm={confirmRemove}
        testId={`${PREFIX}-remove-confirm`}
      />

      <ConfirmDialog
        open={releaseFor !== null}
        onOpenChange={(open) => {
          if (!open && !release.isPending) setReleaseFor(null);
        }}
        title={SSO_COPY.releaseConfirmTitle}
        subject={releaseFor === null ? undefined : employeeFullName(releaseFor)}
        body={SSO_COPY.releaseConfirmBody}
        note={releaseNote}
        confirmLabel={SSO_COPY.releaseConfirm}
        loading={release.isPending}
        onConfirm={() => {
          if (releaseFor !== null) release.mutate(releaseFor.id);
        }}
        testId={`${SSO_RESET_PREFIX}-confirm`}
      />

      {occasionEnabled && (
        <>
          <OccasionDatesDialog
            employee={datesFor}
            onOpenChange={(open) => {
              if (!open) setDatesFor(null);
            }}
            onSaved={invalidateRoster}
            unavailable={occasionUnavailable}
            onUnavailable={() => setOccasionUnavailable(true)}
          />
          <ImportOccasionDatesDialog
            open={importOpen}
            onOpenChange={setImportOpen}
            onImported={invalidateRoster}
            unavailable={occasionUnavailable}
            onUnavailable={() => setOccasionUnavailable(true)}
          />
        </>
      )}
    </div>
  );
}

/**
 * One employee's two-factor standing (AS-WEB5).
 *
 * ⚠ An UNDISCLOSED status (`null`) renders an em-dash and nothing else. It is not "no second
 * factor": the backend withholds the whole column from a caller without `company:edit`, and a build
 * older than AS-BE11 never had it. Rendering it as any real state would put a claim about someone's
 * account security on screen that the server never made.
 */
function MfaStatusCell({
  employee,
  requirementLive,
}: {
  employee: CompanyEmployeeOut;
  requirementLive: boolean;
}) {
  const status = readMfaStatus(employee);
  if (status === null) {
    return (
      <span className="text-text-tertiary" data-testid={`${MFA_PREFIX}-cell-${employee.id}`}>
        —
      </span>
    );
  }
  const { label, tone } = mfaStatusPresentation(status, requirementLive);
  return (
    <Badge tone={tone} dot testId={`${MFA_PREFIX}-cell-${employee.id}`}>
      {label}
    </Badge>
  );
}

/**
 * The compact presence indicator. Both dates absent → a single em-dash, not two.
 *
 * Sighted readers get an icon plus a short form (`Mar 14`); assistive tech gets a visually-hidden
 * span naming the fact in full (`Birthday March 14`). It is written that way rather than as an
 * `aria-label` on the wrapper because a bare `<span>` has role `generic`, which PROHIBITS naming —
 * the label would be silently dropped by real AT even though a testing-library query finds it.
 */
function OccasionDatesCell({ employee }: { employee: CompanyEmployeeOut }) {
  const { birthday, hireDate } = occasionDatesCell(employee);
  if (birthday === null && hireDate === null) {
    return (
      <span className="text-text-tertiary" data-testid={`${OCCASION_PREFIX}-cell-${employee.id}`}>
        —
      </span>
    );
  }
  return (
    <span
      className="flex flex-wrap items-center gap-x-10 gap-y-2 text-text-secondary"
      data-testid={`${OCCASION_PREFIX}-cell-${employee.id}`}
    >
      {birthday !== null && (
        <span className="inline-flex items-center gap-4">
          <Icon icon={Cake} size={13} aria-hidden />
          <span className="sr-only">
            Birthday {formatBirthdayLong(employee.birthday_month, employee.birthday_day)}
          </span>
          <span aria-hidden>{birthday}</span>
        </span>
      )}
      {hireDate !== null && (
        <span className="inline-flex items-center gap-4">
          <Icon icon={Briefcase} size={13} aria-hidden />
          <span className="sr-only">Hire date {formatHireDateLong(employee.hire_date)}</span>
          <span aria-hidden>{hireDate}</span>
        </span>
      )}
    </span>
  );
}
